/**
 * The admin panel.
 *
 * One screen, refreshed on a timer, showing the things that otherwise need SSH
 * and psql: whether the books balance, who is sitting where, what the
 * tournaments are doing. The ledger line is first because it is the only one
 * that can be wrong in a way nobody notices.
 *
 * It authenticates with the session already in this browser. There is no second
 * login and no admin password — the server decides, from a flag on the account,
 * and answers the same way to a missing session, an expired one, and a real
 * session belonging to somebody who is not an admin.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../server.ts';

interface Overview {
  now: number;
  uptimeMs: number;
  ledger: { ok: boolean; total: number; unbalancedTransfers: number; driftedWallets: number };
  players: { accounts: number; bots: number; seated: number; humansSeated: number };
  tables: {
    tableId: string;
    name: string;
    game: 'cash' | 'tournament';
    blinds: string;
    handNumber: number;
    paused: boolean;
    seats: { seat: number; name: string; stack: number; sittingOut: boolean; isBot: boolean }[];
  }[];
  tournaments: {
    id: string;
    name: string;
    status: string;
    registered: number;
    seats: number;
    prizePool: number;
    level: number | null;
    remaining: number | null;
  }[];
}

export function Admin({ session }: { session: string | null }) {
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (session === null) {
      setError('ابتدا وارد شوید.');
      return;
    }
    try {
      const response = await fetch(api('/api/admin/overview'), {
        headers: { authorization: `Bearer ${session}` },
        cache: 'no-store',
      });
      if (!response.ok) {
        setError(response.status === 403 ? 'این حساب دسترسی ادمین ندارد.' : `خطا ${response.status}`);
        return;
      }
      setData((await response.json()) as Overview);
      setError(null);
    } catch {
      setError('سرور در دسترس نیست.');
    }
  }, [session]);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), 5_000);
    return () => clearInterval(timer);
  }, [load]);

  const control = async (tableId: string, action: 'pause' | 'resume'): Promise<void> => {
    if (session === null) return;
    await fetch(api(`/api/admin/tables/${encodeURIComponent(tableId)}/${action}`), {
      method: 'POST',
      headers: { authorization: `Bearer ${session}` },
    });
    await load();
  };

  if (error !== null) {
    return (
      <div className="lobby">
        <h1>Admin</h1>
        <div className="error">{error}</div>
        <p className="sub">
          <a href="/">بازگشت به اتاق</a>
        </p>
      </div>
    );
  }
  if (data === null) return <div className="lobby"><p className="sub">در حال خواندن…</p></div>;

  return (
    <div className="lobby admin">
      <div className="account-bar">
        <span className="who">Admin</span>
        <span className="spacer" />
        <span className="t-meta num lat-ui">up {formatUptime(data.uptimeMs)}</span>
        <a className="btn-plain small" href="/">
          Back to the Room
        </a>
      </div>

      {/* First, because it is the only number here that can be wrong in a way
          nobody notices until it is far too late to reconstruct. */}
      <div className={`ledger-line${data.ledger.ok ? ' ok' : ' bad'}`}>
        <span className="k lat-ui">Ledger</span>
        <span className="v lat-ui">{data.ledger.ok ? 'balanced' : 'OUT OF BALANCE'}</span>
        <span className="detail lat-ui">
          total {data.ledger.total} · unbalanced {data.ledger.unbalancedTransfers} · drifted{' '}
          {data.ledger.driftedWallets}
        </span>
      </div>

      <div className="stat-row">
        <Stat label="Accounts" value={data.players.accounts} />
        <Stat label="Bots" value={data.players.bots} />
        <Stat label="Seated" value={data.players.seated} />
        <Stat label="People Seated" value={data.players.humansSeated} accent />
      </div>

      <h2 className="section lat-ui">Tables</h2>
      <div className="admin-tables">
        {data.tables.map((table) => (
          <div key={table.tableId} className="admin-table">
            <div className="head">
              <span className="t-name">{table.name}</span>
              <span className="t-meta num lat-ui">
                {table.blinds} · hand #{table.handNumber}
              </span>
              {table.paused && <span className="tag">PAUSED</span>}
              <span className="spacer" />
              <button
                className="btn-plain small"
                onClick={() => void control(table.tableId, table.paused ? 'resume' : 'pause')}
              >
                {table.paused ? 'Resume' : 'Pause'}
              </button>
            </div>
            {table.seats.length === 0 ? (
              <p className="sng-note">خالی.</p>
            ) : (
              <div className="admin-seats">
                {table.seats.map((seat) => (
                  <div key={seat.seat} className="admin-seat">
                    <span className="place lat-ui">{seat.seat}</span>
                    <span className="who">
                      {seat.name}
                      {seat.isBot && <span className="tag bot lat-ui">BOT</span>}
                      {seat.sittingOut && <span className="tag">OUT</span>}
                    </span>
                    <span className="prize lat-ui">{seat.stack.toLocaleString('en-US')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <h2 className="section lat-ui">Sit &amp; Go</h2>
      <div className="admin-tables">
        {data.tournaments.map((game) => (
          <div key={game.id} className="admin-table">
            <div className="head">
              <span className="t-name">{game.name}</span>
              <span className="t-meta num lat-ui">
                {game.status} · {game.registered}/{game.seats} · pool{' '}
                {game.prizePool.toLocaleString('en-US')}
                {game.level !== null && ` · level ${game.level}`}
                {game.remaining !== null && ` · ${game.remaining} left`}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className="figure">
      <span className="f-label lat-ui">{label}</span>
      <span className={`f-value lat-ui${accent === true ? ' accent' : ''}`}>
        {value.toLocaleString('en-US')}
      </span>
    </div>
  );
}

function formatUptime(ms: number): string {
  const total = Math.floor(ms / 1000);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}
